import Vue from 'vue'
import Vuex from 'vuex'

import config from 'SrcConfig'

import actions from './actions'
import mutations from './mutations'
import getters from './getters'

import createLogger from './plugins/logger'
import { loadTypes } from '@/lib/constants'

Vue.use(Vuex)

const debug = !['release', 'staging'].includes(process.env.NODE_ENV)

export default new Vuex.Store({
  state: {
    $account: null,
    $permissions: null,
    $locale: config.locale,
    $uploadProgress: 0,
    $downloadProgress: 0,
    $offline: false,
    breadcrumb: [],
    appVersion: process.env.PACKAGE_VERSION || 0,
    fingerprint: null,
    clientType: null,
    webView: false,
    firebaseMessagingToken: null,
    order: {
      types: [
        {
          id: 10,
          title: loadTypes.furniture.title,
          slug: 'furniture',
          image: 'furniture.svg',
          themeColor: 'yellow',
          steps: [
            {
              progress: 0,
              templateProgress: 0,
              hideInTemplate: false,
              slug: 'location-selection',
              title: 'انتخاب محل',
              defaultIcon: 'activeSelectLocation.svg',
              activeIcon: 'activeSelectLocation.svg',
              config: {
                type: 'map-based',
                showPlans: true,
                includeOrigin: true,
                originCoordinatesRequired: true,
                includeDestination: true,
                destinationCoordinatesRequired: true,
                showRangeMessage: true,
                rangeMessage: {
                  100: '', // this cause no message shown in urban range
                  200: 'این درخواست با قوانین و تعرفه <b>بین شهری</b> محاسبه خواهد شد'
                },
                origin: {
                  title: 'مبدا',
                  markerIcon: 'originMap.png',
                  fields: {
                    floor: 'digit|required|min_value:-10|max_value:100',
                    elevator: 'required',
                    carDistanceMeter: 'required'
                  }
                },
                destination: {
                  title: 'مقصد',
                  markerIcon: 'destination.svg',
                  fields: {
                    floor: 'digit|required|min_value:-10|max_value:100',
                    elevator: 'required',
                    carDistanceMeter: 'required'
                  }
                },
                multiDestination: false,
                showCompleteAddressButton: true,
                showCompleteAddressModalBeforeNext: true,
                manualCitySelectionOnException: true,
                validation: [],
                stepToGoOnValidationFailed: 'location-selection'
              }
            },
            {
              progress: 33,
              templateProgress: 50,
              hideInTemplate: false,
              slug: 'property-selection',
              title: 'تعیین ویژگی ها',
              defaultIcon: 'deactiveSelectItem.svg',
              activeIcon: 'activeSelectItem.svg',
              config: {
                indexSection: 'timing',
                templateIndexSection: 'car',
                sectionHeader: {
                  rangeToShow: 100
                },
                sections: [
                  {
                    title: 'تاریخ و زمان',
                    slug: 'timing',
                    showBrief: false,
                    hideInTemplate: true,
                    fields: {
                      orderDate: {
                        title: 'تاریخ اسباب‌کشی',
                        validation: 'required'
                      },
                      orderTime: {
                        title: 'ساعت اسباب‌کشی',
                        validation: 'required'
                      }
                    }
                  },
                  {
                    title: 'خودرو',
                    slug: 'car',
                    type: 'laborer-based',
                    showBrief: true,
                    hideInTemplate: false,
                    description: {
                      rangeToShow: 200,
                      text: 'کارگران حمل بار در شهر مقصد به عهده صاحب بار میباشد.'
                    }
                  },
                  {
                    title: 'نیروی فنی',
                    slug: 'technician',
                    showBrief: true,
                    hideInTemplate: false
                  },
                  {
                    title: 'اقلام هزینه دار',
                    slug: 'costly-items',
                    showBrief: true,
                    hideInTemplate: false
                  }
                ],
                validation: [
                  {
                    point: 'origin',
                    fields: ['latitude', 'longitude', 'floor']
                  },
                  {
                    point: 'destination',
                    fields: ['latitude', 'longitude', 'floor']
                  }
                ],
                templateValidation: [
                  {
                    point: 'origin',
                    fields: ['latitude', 'longitude', 'floor']
                  },
                  {
                    point: 'destination',
                    fields: ['latitude', 'longitude', 'floor']
                  }
                ],
                stepToGoOnValidationFailed: 'location-selection',
                templateStepToGoOnValidationFailed: 'location-selection'
              }
            },
            {
              progress: 66,
              hideInTemplate: true,
              slug: 'company-selection',
              title: 'انتخاب شرکت (باربری)',
              defaultIcon: 'deactiveSelectShipment.svg',
              activeIcon: 'activeSelectShipment.svg',
              config: {
                initialSelectsCount: 5,
                multiSelect: true,
                noResultMessage:
                  'متاسفانه هیچ باربری ای مطابق با درخواست شما پیدا نشد!',
                sortsByRange: {
                  100: [
                    'popular',
                    'name',
                    'cheapest',
                    'most-expensive',
                    'nearest'
                  ],
                  200: ['popular', 'name', 'nearest']
                },
                defaultSort: {
                  100: 'cheapest',
                  200: 'popular'
                },
                description:
                  'لطفا یک یا چند باربری مورد نظر خود را برای ثبت نهایی انتخاب نمایید',
                validation: [
                  {
                    point: 'origin',
                    fields: ['latitude', 'longitude', 'floor']
                  },
                  {
                    point: 'destination',
                    fields: ['latitude', 'longitude', 'floor']
                  },
                  'orderTime',
                  {
                    or: ['car', 'employee']
                  }
                ],
                templateValidation: [
                  {
                    point: 'origin',
                    fields: ['latitude', 'longitude', 'floor']
                  },
                  {
                    point: 'destination',
                    fields: ['latitude', 'longitude', 'floor']
                  },
                  {
                    or: ['car', 'employee']
                  }
                ],
                stepToGoOnValidationFailed: 'property-selection',
                sectionToGoOnValidationFailed: 'timing'
              }
            },
            {
              progress: 100,
              templateProgress: 100,
              hideInTemplate: false,
              slug: 'overview',
              title: 'بررسی اجمالی',
              defaultIcon: 'deactiveCompleteAddress.svg',
              activeIcon: 'activeCompleteAddress.svg',
              config: {
                readOnlyFields: {
                  orderDate: 'تاریخ اسباب‌کشی',
                  orderTime: 'ساعت اسباب‌کشی',
                  origin: {
                    floor: 'طبقه',
                    elevator: 'آسانسور حمل بار',
                    carDistanceMeter: 'فاصله درب تا خودرو'
                  },
                  destination: {
                    floor: 'طبقه',
                    elevator: 'آسانسور حمل بار',
                    carDistanceMeter: 'فاصله درب تا خودرو'
                  },
                  employee: {
                    title: 'نیروی فنی',
                    colClass: 'col-lg-6'
                  },
                  car: {
                    title: 'خودرو',
                    colClass: 'col-lg-6'
                  },
                  item: {
                    title: 'اقلام هزینه دار',
                    colClass: ''
                  }
                },
                templateReadOnlyFields: {
                  origin: {
                    floor: 'طبقه',
                    elevator: 'آسانسور حمل بار',
                    carDistanceMeter: 'فاصله درب تا خودرو'
                  },
                  destination: {
                    floor: 'طبقه',
                    elevator: 'آسانسور حمل بار',
                    carDistanceMeter: 'فاصله درب تا خودرو'
                  },
                  employee: {
                    title: 'نیروی فنی',
                    colClass: 'col-lg-6'
                  },
                  car: {
                    title: 'خودرو',
                    colClass: 'col-lg-6'
                  },
                  item: {
                    title: 'اقلام هزینه دار',
                    colClass: ''
                  }
                },
                inputs: {
                  origin: {
                    name: {
                      title: 'تحویل دهنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-xl-2'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-xl-4'
                    },
                    plate: {
                      title: 'پلاک',
                      validation: 'required|digit',
                      colClass: 'col-xl-2'
                    },
                    unit: {
                      title: 'واحد',
                      validation: '',
                      colClass: 'col-xl-2'
                    },
                    weighbridgeWeightKilogram: {
                      title: 'وزن باسکول در مبدا',
                      validation: ''
                    },
                    weighbridgeReceiptFile: {
                      title: 'رسید باسکول در مبدا',
                      validation: ''
                    }
                  },
                  destination: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه ',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-xl-2'
                    },
                    address: {
                      title: 'آدرس ',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-xl-4'
                    },
                    plate: {
                      title: 'پلاک ',
                      validation: 'digit',
                      colClass: 'col-lg-2 col-xl-2'
                    },
                    unit: {
                      title: 'واحد ',
                      validation: '',
                      colClass: 'col-xl-2'
                    },
                    weighbridgeWeightKilogram: {
                      title: 'وزن باسکول در مقصد',
                      validation: ''
                    },
                    weighbridgeReceiptFile: {
                      title: 'رسید باسکول در مقصد',
                      validation: ''
                    }
                  },
                  description: {
                    title: 'توضیحات',
                    validation: ''
                  },
                  attachments: {
                    title: 'پیوست ها',
                    validation: ''
                  },
                  image: {
                    title: 'تصویر',
                    validation: ''
                  }
                },
                templateInputs: {
                  templateTitle: {
                    title: 'عنوان الگو',
                    validation: 'required'
                  },
                  origin: {
                    name: {
                      title: 'تحویل دهنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-xl-2'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-xl-6'
                    },
                    plate: {
                      title: 'پلاک',
                      validation: 'required|digit',
                      colClass: 'col-xl-1'
                    },
                    unit: {
                      title: 'واحد',
                      validation: '',
                      colClass: 'col-xl-1'
                    }
                  },
                  destination: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه ',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-xl-2'
                    },

                    address: {
                      title: 'آدرس ',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lx-6'
                    },
                    plate: {
                      title: 'پلاک ',
                      validation: 'digit',
                      colClass: 'col-xl-1'
                    },
                    unit: {
                      title: 'واحد ',
                      validation: '',
                      colClass: 'col-xl-1'
                    }
                  },
                  description: {
                    title: 'توضیحات',
                    validation: ''
                  }
                },
                showPointDivider: true,
                originColClass: '',
                destinationColClass: '',
                validation: [
                  {
                    point: 'origin',
                    fields: ['latitude', 'longitude', 'floor']
                  },
                  {
                    point: 'destination',
                    fields: ['latitude', 'longitude', 'floor']
                  },
                  'orderTime',
                  {
                    or: ['car', 'employee']
                  },
                  'selectedCourierIds'
                ],
                templateValidation: [
                  {
                    point: 'origin',
                    fields: ['latitude', 'longitude', 'floor']
                  },
                  {
                    point: 'destination',
                    fields: ['latitude', 'longitude', 'floor']
                  },
                  {
                    or: ['car', 'employee']
                  }
                ],
                stepToGoOnValidationFailed: 'company-selection',
                templateStepToGoOnValidationFailed: 'property-selection',
                templateSectionToGoOnValidationFailed: 'car'
              }
            },
            {
              progress: null,
              templateProgress: null,
              hideInTemplate: true,
              slug: 'success',
              title: 'ثبت موفقیت آمیز',
              config: {
                validation: ['acceptedOffers'],
                stepToGoOnValidationFailed: 'overview'
              }
            }
          ],
          enabled: true
        },
        {
          id: 20,
          title: loadTypes.cargo.title,
          slug: 'cargo',
          image: 'cargo.svg',
          themeColor: 'green',
          steps: [
            {
              progress: 0,
              templateProgress: 0,
              hideInTemplate: false,
              slug: 'location-selection',
              title: 'انتخاب محل',
              defaultIcon: 'activeSelectLocation.svg',
              activeIcon: 'activeSelectLocation.svg',
              config: {
                type: 'selection-based',
                showPlans: false,
                includeOrigin: true,
                originRequired: true,
                originCoordinatesRequired: false,
                includeDestination: true,
                destinationRequired: true,
                destinationCoordinatesRequired: false,
                showRangeMessage: false,
                rangeMessage: {
                  100: 'این درخواست با تعرفه ی <b>شهری</b> محاسبه خواهد شد',
                  200: 'این درخواست با تعرفه ی <b>بین شهری</b> محاسبه خواهد شد'
                },
                origin: {
                  title: 'مبدا',
                  markerIcon: 'originMap.png',
                  fields: {
                    address: 'min:10'
                  }
                },
                destination: {
                  title: 'مقصد',
                  markerIcon: 'destination.svg',
                  fields: {
                    address: 'min:10'
                  }
                },
                multiDestination: false,
                showCompleteAddressButton: false,
                showCompleteAddressModalBeforeNext: false,
                manualCitySelectionOnException: true,
                validation: [],
                stepToGoOnValidationFailed: 'location-selection'
              }
            },
            {
              progress: 33,
              templateProgress: 50,
              hideInTemplate: false,
              slug: 'property-selection',
              title: 'تعیین ویژگی ها',
              defaultIcon: 'deactiveSelectItem.svg',
              activeIcon: 'activeSelectItem.svg',
              config: {
                indexSection: 'timing',
                templateIndexSection: 'specification',
                withSectionHeader: false,
                sections: [
                  {
                    title: 'تاریخ و زمان',
                    slug: 'timing',
                    showBrief: false,
                    hideInTemplate: true,
                    fields: {
                      orderDate: {
                        title: 'تاریخ بارگیری',
                        validation: 'required'
                      },
                      orderTime: {
                        title: 'ساعت بارگیری',
                        validation: 'required'
                      },
                      dischargeDate: {
                        title: 'تاریخ تخلیه',
                        validation: 'required'
                      },
                      dischargeDayPart: {
                        title: 'زمان تخلیه',
                        validation: 'required'
                      }
                    }
                  },
                  {
                    title: 'مشخصات بار',
                    slug: 'specification',
                    showBrief: true,
                    hideInTemplate: false,
                    fields: {
                      loadType: {
                        title: 'نوع بار',
                        validation: 'required'
                      },
                      packageType: {
                        title: 'نوع بسته‌بندی',
                        validation: 'required'
                      },
                      monetaryValue: {
                        title: 'ارزش بار',
                        validation:
                          'required|decimal|min_value: 1|validCurrency'
                      },
                      weightKilogram: {
                        title: 'وزن بار',
                        validation: 'required|min_value:0.1|max_value:100'
                      },
                      loadDocument: {
                        title: 'نوع سند حمل',
                        validation: 'required'
                      }
                    }
                  },
                  {
                    title: 'خودرو',
                    slug: 'car',
                    type: 'load-based',
                    showBrief: true,
                    hideInTemplate: false
                  }
                ],
                validation: [
                  {
                    point: 'origin',
                    fields: ['cityId']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId']
                  }
                ],
                templateValidation: [
                  {
                    point: 'origin',
                    fields: ['cityId']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId']
                  }
                ],
                stepToGoOnValidationFailed: 'location-selection',
                templateStepToGoOnValidationFailed: 'location-selection'
              }
            },
            {
              progress: 66,
              hideInTemplate: true,
              slug: 'company-selection',
              title: 'انتخاب شرکت (باربری)',
              defaultIcon: 'deactiveSelectShipment.svg',
              activeIcon: 'activeSelectShipment.svg',
              config: {
                initialSelectsCount: 5,
                multiSelect: true,
                noResultMessage:
                  'متاسفانه هیچ باربری ای مطابق با درخواست شما پیدا نشد!',
                sortsByRange: {
                  100: ['popular', 'name', 'nearest'],
                  200: ['popular', 'name', 'nearest']
                },
                defaultSort: {
                  100: 'popular',
                  200: 'popular'
                },
                description:
                  'لطفا یک یا چند باربری مورد نظر خود را برای ثبت نهایی انتخاب نمایید',
                validation: [
                  {
                    point: 'origin',
                    fields: ['cityId']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId']
                  },
                  'orderTime',
                  'dischargeTime',
                  'dischargeDayPartId',
                  'loadId',
                  'packageId',
                  'monetaryValue',
                  'weightKilogram',
                  'car'
                ],
                stepToGoOnValidationFailed: 'property-selection',
                sectionToGoOnValidationFailed: 'timing'
              }
            },
            {
              progress: 100,
              templateProgress: 100,
              hideInTemplate: false,
              slug: 'overview',
              title: 'بررسی اجمالی',
              defaultIcon: 'deactiveCompleteAddress.svg',
              activeIcon: 'activeCompleteAddress.svg',
              config: {
                readOnlyFields: {
                  orderDate: 'تاریخ بارگیری',
                  orderTime: 'ساعت بارگیری',
                  dischargeDate: 'تاریخ تخلیه',
                  dischargeDayPart: 'زمان تخلیه',
                  car: {
                    title: 'خودرو',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  carLoad: {
                    title: 'نوع بارگیر',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  load: {
                    title: 'نوع بار',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  packageType: {
                    title: 'بسته‌بندی',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  // dimensions: {
                  //   title: 'ابعاد',
                  //   colClass: 'col-md-6 col-lg-3'
                  // },
                  weight: {
                    title: 'وزن بار',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  monetaryValue: {
                    title: 'ارزش بار',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  document: {
                    title: 'نوع سند حمل',
                    colClass: 'col-md-6 col-lg-3'
                  }
                },
                templateReadOnlyFields: {
                  origin: {
                    address: 'محل بارگیری'
                  },
                  destination: {
                    address: 'محل تخلیه'
                  },
                  car: {
                    title: 'خودرو',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  carLoad: {
                    title: 'نوع بارگیر',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  load: {
                    title: 'نوع بار',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  packageType: {
                    title: 'بسته‌بندی',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  // dimensions: {
                  //   title: 'ابعاد',
                  //   colClass: 'col-md-6 col-lg-3'
                  // },
                  weight: {
                    title: 'وزن بار',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  monetaryValue: {
                    title: 'ارزش بار',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  document: {
                    title: 'نوع سند حمل',
                    colClass: 'col-md-6 col-lg-3'
                  }
                },
                inputs: {
                  origin: {
                    name: {
                      title: 'تحویل دهنده',
                      validation: 'required',
                      colClass: 'col-md-6'
                    },
                    mobile: {
                      title: 'تلفن همراه',
                      validation: 'required|mobile',
                      colClass: 'col-md-6'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12'
                    },
                    weighbridgeWeightKilogram: {
                      title: 'وزن باسکول در مبدا',
                      validation: ''
                    },
                    weighbridgeReceiptFile: {
                      title: 'رسید باسکول در مبدا',
                      validation: ''
                    }
                  },
                  destination: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6'
                    },
                    mobile: {
                      title: 'تلفن همراه ',
                      validation: 'required|mobile',
                      colClass: 'col-md-6'
                    },
                    address: {
                      title: 'آدرس مقصد',
                      validation: 'required|min:10',
                      colClass: 'col-md-12'
                    },
                    weighbridgeWeightKilogram: {
                      title: 'وزن باسکول در مقصد',
                      validation: ''
                    },
                    weighbridgeReceiptFile: {
                      title: 'رسید باسکول در مقصد',
                      validation: ''
                    }
                  },
                  description: {
                    title: 'توضیحات',
                    validation: '',
                    colClass: ''
                  },
                  attachments: {
                    title: 'پیوست ها',
                    validation: ''
                  },
                  image: {
                    title: 'تصویر',
                    validation: ''
                  }
                },
                templateInputs: {
                  templateTitle: {
                    title: 'عنوان الگو',
                    validation: 'required'
                  },
                  origin: {
                    name: {
                      title: 'تحویل دهنده',
                      validation: 'required',
                      colClass: 'col-md-6'
                    },
                    mobile: {
                      title: 'تلفن همراه',
                      validation: 'required|mobile',
                      colClass: 'col-md-6'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12'
                    }
                  },
                  destination: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6'
                    },
                    mobile: {
                      title: 'تلفن همراه ',
                      validation: 'required|mobile',
                      colClass: 'col-md-6'
                    },
                    address: {
                      title: 'آدرس مقصد',
                      validation: 'required|min:10',
                      colClass: 'col-md-12'
                    }
                  },
                  description: {
                    title: 'توضیحات',
                    validation: '',
                    colClass: ''
                  }
                },
                showPointDivider: false,
                originColClass: 'col-lg-6',
                destinationColClass: 'col-lg-6',
                validation: [
                  {
                    point: 'origin',
                    fields: ['cityId']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId']
                  },
                  'orderTime',
                  'dischargeTime',
                  'dischargeDayPartId',
                  'loadId',
                  'packageId',
                  'monetaryValue',
                  'weightKilogram',
                  'car',
                  'selectedCourierIds'
                ],
                templateValidation: [
                  {
                    point: 'origin',
                    fields: ['cityId']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId']
                  },
                  'loadId',
                  'packageId',
                  'monetaryValue',
                  'weightKilogram',
                  'car'
                ],
                stepToGoOnValidationFailed: 'company-selection',
                templateStepToGoOnValidationFailed: 'property-selection',
                templateSectionToGoOnValidationFailed: 'specification'
              }
            },
            {
              progress: null,
              templateProgress: null,
              hideInTemplate: true,
              slug: 'success',
              title: 'ثبت موفقیت آمیز',
              config: {
                validation: ['acceptedOffers'],
                stepToGoOnValidationFailed: 'overview'
              }
            }
          ],
          enabled: true
        },
        {
          id: 30,
          title: loadTypes.postage.title,
          slug: 'postal-postage',
          image: 'postage.svg',
          themeColor: 'postal-postage',
          steps: [
            {
              progress: 0,
              templateProgress: 0,
              hideInTemplate: false,
              slug: 'location-selection',
              title: 'انتخاب محل',
              defaultIcon: 'step-1-deactive-dark.svg',
              activeIcon: 'deactiveSelectLocation.svg',
              config: {
                type: 'selection-based',
                showPlans: false,
                includeOrigin: true,
                originRequired: true,
                originCoordinatesRequired: false,
                includeDestination: true,
                destinationRequired: true,
                destinationCoordinatesRequired: false,
                showRangeMessage: false,
                rangeMessage: {
                  100: 'این درخواست با تعرفه ی <b>شهری</b> محاسبه خواهد شد',
                  200: 'این درخواست با تعرفه ی <b>بین شهری</b> محاسبه خواهد شد'
                },
                origin: {
                  title: 'مبدا',
                  markerIcon: 'originMap.png',
                  fields: {
                    address: 'min:10',
                    plate: 'digit',
                    floor: 'digit|min_value:-10|max_value:100',
                    unit: '',
                    zipCode: 'zipCode'
                  }
                },
                destination: {
                  title: 'مقصد',
                  markerIcon: 'destination.svg',
                  fields: {
                    address: 'min:10',
                    plate: 'digit',
                    floor: 'digit|min_value:-10|max_value:100',
                    unit: '',
                    zipCode: 'zipCode'
                  }
                },
                multiDestination: false,
                showCompleteAddressButton: false,
                showCompleteAddressModalBeforeNext: false,
                manualCitySelectionOnException: true,
                validation: [],
                stepToGoOnValidationFailed: 'location-selection'
              }
            },
            {
              progress: 25,
              templateProgress: 50,
              hideInTemplate: false,
              slug: 'property-selection',
              title: 'تعیین ویژگی ها',
              defaultIcon: 'step-2-deactive-dark.svg',
              activeIcon: 'deactiveSelectItem.svg',
              config: {
                indexSection: 'specification',
                templateIndexSection: 'specification',
                withSectionHeader: false,
                sections: [
                  {
                    title: 'مشخصات مرسوله',
                    slug: 'specification',
                    showBrief: true,
                    hideInTemplate: false,
                    fields: {
                      packagge: {
                        title: '',
                        validation: 'required'
                      }
                    }
                  }
                ],
                validation: [
                  {
                    point: 'origin',
                    fields: ['cityId']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId']
                  }
                ],
                templateValidation: [
                  {
                    point: 'origin',
                    fields: ['cityId']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId']
                  }
                ],
                stepToGoOnValidationFailed: 'location-selection',
                templateStepToGoOnValidationFailed: 'location-selection'
              }
            },
            {
              progress: 50,
              hideInTemplate: true,
              slug: 'company-selection',
              title: 'انتخاب شرکت (باربری)',
              defaultIcon: 'step-3-deactive-dark.svg',
              activeIcon: 'deactiveSelectShipment.svg',
              config: {
                initialSelectsCount: 0,
                multiSelect: false,
                includeCompanyShift: true,
                noResultMessage:
                  'متاسفانه هیچ باربری ای مطابق با درخواست شما پیدا نشد!',
                sortsByRange: {
                  100: ['popular', 'name', 'cheapest', 'most-expensive'],
                  200: ['popular', 'name', 'cheapest', 'most-expensive']
                },
                defaultSort: {
                  100: 'cheapest',
                  200: 'cheapest'
                },
                description:
                  'لطفا باربری مورد نظر خود را برای ثبت نهایی انتخاب نمایید',
                validation: [
                  {
                    point: 'origin',
                    fields: ['cityId']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId']
                  },
                  'package'
                ],
                stepToGoOnValidationFailed: 'property-selection',
                sectionToGoOnValidationFailed: 'specification'
              }
            },
            {
              progress: 75,
              templateProgress: 100,
              hideInTemplate: false,
              slug: 'overview',
              title: 'بررسی اجمالی',
              defaultIcon: 'step-3-deactive-dark.svg',
              activeIcon: 'deactiveSelectShipment.svg',
              config: {
                readOnlyFields: {
                  packagge: {
                    title: 'مشخصات مرسوله',
                    colClass: ''
                  }
                },
                templateReadOnlyFields: {
                  packagge: {
                    title: 'مشخصات مرسوله',
                    colClass: ''
                  }
                },
                inputs: {
                  origin: {
                    name: {
                      title: 'تحویل دهنده',
                      validation: 'required',
                      colClass: 'col-md-6'
                    },
                    mobile: {
                      title: 'تلفن همراه',
                      validation: 'required|mobile',
                      colClass: 'col-md-6'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12'
                    },
                    plate: {
                      title: 'پلاک',
                      validation: 'required|digit',
                      colClass: 'col-md-3'
                    },
                    floor: {
                      title: 'طبقه',
                      validation: 'digit|min_value:-10|max_value:100',
                      colClass: 'col-md-3'
                    },
                    unit: {
                      title: 'واحد',
                      validation: '',
                      colClass: 'col-md-3'
                    },
                    zipCode: {
                      title: 'کد‌پستی',
                      validation: 'required|zipCode',
                      colClass: 'col-md-3'
                    }
                  },
                  destination: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6'
                    },
                    mobile: {
                      title: ' تلفن همراه',
                      validation: 'required|mobile',
                      colClass: 'col-md-6'
                    },
                    address: {
                      title: 'آدرس مقصد',
                      validation: 'required|min:10',
                      colClass: 'col-md-12'
                    },
                    plate: {
                      title: ' پلاک',
                      validation: 'required|digit',
                      colClass: 'col-md-3'
                    },
                    floor: {
                      title: 'طبقه ',
                      validation: 'digit|min_value:-10|max_value:100',
                      colClass: 'col-md-3'
                    },
                    unit: {
                      title: 'واحد ',
                      validation: '',
                      colClass: 'col-md-3'
                    },
                    zipCode: {
                      title: 'کد‌پستی ',
                      validation: 'required|zipCode',
                      colClass: 'col-md-3'
                    }
                  },
                  description: {
                    title: 'توضیحات',
                    validation: '',
                    colClass: ''
                  },
                  attachments: {
                    title: 'پیوست ها',
                    validation: ''
                  },
                  image: {
                    title: 'تصویر',
                    validation: ''
                  }
                },
                templateInputs: {
                  templateTitle: {
                    title: 'عنوان الگو',
                    validation: 'required'
                  },
                  origin: {
                    name: {
                      title: 'تحویل دهنده',
                      validation: 'required',
                      colClass: 'col-md-6'
                    },
                    mobile: {
                      title: 'تلفن همراه',
                      validation: 'required|mobile',
                      colClass: 'col-md-6'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12'
                    },
                    plate: {
                      title: 'پلاک',
                      validation: 'required|digit',
                      colClass: 'col-md-3'
                    },
                    floor: {
                      title: 'طبقه',
                      validation: 'digit|min_value:-10|max_value:100',
                      colClass: 'col-md-3'
                    },
                    unit: {
                      title: 'واحد',
                      validation: '',
                      colClass: 'col-md-3'
                    },
                    zipCode: {
                      title: 'کد‌پستی',
                      validation: 'required|zipCode',
                      colClass: 'col-md-3'
                    }
                  },
                  destination: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6'
                    },
                    mobile: {
                      title: ' تلفن همراه',
                      validation: 'required|mobile',
                      colClass: 'col-md-6'
                    },
                    address: {
                      title: 'آدرس مقصد',
                      validation: 'required|min:10',
                      colClass: 'col-md-12'
                    },
                    plate: {
                      title: ' پلاک',
                      validation: 'required|digit',
                      colClass: 'col-md-3'
                    },
                    floor: {
                      title: 'طبقه ',
                      validation: 'digit|min_value:-10|max_value:100',
                      colClass: 'col-md-3'
                    },
                    unit: {
                      title: 'واحد ',
                      validation: '',
                      colClass: 'col-md-3'
                    },
                    zipCode: {
                      title: 'کد‌پستی ',
                      validation: 'required|zipCode',
                      colClass: 'col-md-3'
                    }
                  },
                  description: {
                    title: 'توضیحات',
                    validation: '',
                    colClass: ''
                  }
                },
                showPointDivider: true,
                originColClass: 'col-12',
                destinationColClass: 'col-12',
                validation: [
                  {
                    point: 'origin',
                    fields: ['cityId']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId']
                  },
                  'selectedCourierIds',
                  'selectedTimeSheetTemplate',
                  'packagge'
                ],
                templateValidation: [
                  {
                    point: 'origin',
                    fields: ['cityId']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId']
                  },
                  'packagge'
                ],
                stepToGoOnValidationFailed: 'company-selection',
                templateStepToGoOnValidationFailed: 'property-selection',
                templateSectionToGoOnValidationFailed: 'specification'
              }
            },
            {
              progress: 100,
              templateProgress: 100,
              hideInTemplate: true,
              slug: 'pay',
              title: 'پرداخت',
              defaultIcon: 'step-4-deactive-dark.svg',
              activeIcon: 'deactiveCompleteAddress.svg',
              config: {
                validation: [
                  {
                    point: 'origin',
                    fields: ['cityId']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId']
                  },
                  'selectedCourierIds',
                  'selectedTimeSheetTemplate',
                  'packagge',
                  'acceptedOffers'
                ],
                templateValidation: [],
                stepToGoOnValidationFailed: 'overview',
                templateStepToGoOnValidationFailed: null,
                templateSectionToGoOnValidationFailed: null
              }
            },
            {
              progress: null,
              templateProgress: null,
              hideInTemplate: true,
              slug: 'success',
              title: 'ثبت موفقیت آمیز',
              config: {
                validation: ['acceptedOffers'],
                stepToGoOnValidationFailed: 'overview'
              }
            }
          ],
          enabled: true
        },
        {
          id: 40,
          title: loadTypes.delivery.title,
          slug: 'delivery',
          image: 'postage.svg',
          themeColor: 'delivery',
          steps: [
            {
              progress: 0,
              templateProgress: 0,
              hideInTemplate: false,
              slug: 'location-selection',
              title: 'انتخاب محل',
              defaultIcon: 'activeSelectLocation.svg',
              activeIcon: 'activeSelectLocation.svg',
              config: {
                type: 'map-based',
                showPlans: true,
                includeOrigin: true,
                originCoordinatesRequired: true,
                includeDestination: true,
                destinationCoordinatesRequired: true,
                showRangeMessage: true,
                rangeMessage: {
                  100: '', // this cause no message shown in urban range
                  200: 'این درخواست با قوانین و تعرفه <b>بین شهری</b> محاسبه خواهد شد'
                },
                origin: {
                  title: 'مبدا',
                  markerIcon: 'originMap.png',
                  fields: {
                    address: 'min:10'
                  }
                },
                destination: {
                  title: 'مقصد',
                  markerIcon: 'destination.svg',
                  fields: {
                    address: 'min:10'
                  }
                },
                multiDestination: true,
                showCompleteAddressButton: false,
                showCompleteAddressModalBeforeNext: false,
                manualCitySelectionOnException: false,
                validation: [],
                stepToGoOnValidationFailed: 'location-selection'
              }
            },
            {
              progress: 25,
              templateProgress: 50,
              hideInTemplate: false,
              slug: 'property-selection',
              title: 'تعیین ویژگی ها',
              defaultIcon: 'deactiveSelectShipment.svg',
              activeIcon: 'activeSelectShipment.svg',
              config: {
                indexSection: 'specification',
                templateIndexSection: 'specification',
                withSectionHeader: false,
                sections: [
                  {
                    title: 'مشخصات',
                    slug: 'specification',
                    showBrief: true,
                    hideInTemplate: false,
                    fields: {
                      waitMinute: {
                        title: 'توقف در محل',
                        validation: ''
                      },
                      roundTrip: {
                        title: 'بازگشت به مبدأ',
                        validation: ''
                      },
                      reserveOrder: {
                        title: 'رزرو درخواست برای آینده',
                        validation: ''
                      },
                      orderDate: {
                        title: 'تاریخ رزرو',
                        validation: 'required'
                      },
                      orderTime: {
                        title: 'ساعت رزرو',
                        validation: 'required'
                      }
                    }
                  }
                ],
                validation: [
                  {
                    point: 'origin',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId', 'latitude', 'longitude']
                  }
                ],
                templateValidation: [
                  {
                    point: 'origin',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId', 'latitude', 'longitude']
                  }
                ],
                stepToGoOnValidationFailed: 'location-selection',
                templateStepToGoOnValidationFailed: 'location-selection'
              }
            },
            {
              progress: 50,
              hideInTemplate: true,
              slug: 'company-selection',
              title: 'انتخاب شرکت (باربری)',
              defaultIcon: 'deactiveSelectShipment.svg',
              activeIcon: 'activeSelectShipment.svg',
              config: {
                initialSelectsCount: 0,
                multiSelect: false,
                noResultMessage:
                  'متاسفانه هیچ باربری ای مطابق با درخواست شما پیدا نشد!',
                sortsByRange: {
                  100: ['popular', 'name', 'cheapest', 'most-expensive'],
                  200: ['popular', 'name', 'cheapest', 'most-expensive']
                },
                defaultSort: {
                  100: 'cheapest',
                  200: 'popular'
                },
                description:
                  'لطفا باربری مورد نظر خود را برای ثبت نهایی انتخاب نمایید',
                validation: [
                  {
                    point: 'origin',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId', 'latitude', 'longitude']
                  }
                ],
                templateValidation: [
                  {
                    point: 'origin',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId', 'latitude', 'longitude']
                  }
                ],
                stepToGoOnValidationFailed: 'property-selection',
                sectionToGoOnValidationFailed: 'specification'
              }
            },
            {
              progress: 75,
              templateProgress: 100,
              hideInTemplate: false,
              slug: 'overview',
              title: 'بررسی اجمالی',
              defaultIcon: 'deactiveCompleteAddress.svg',
              activeIcon: 'activeCompleteAddress.svg',
              config: {
                readOnlyFields: {
                  waitMinute: {
                    title: 'توقف در محل',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  roundTrip: {
                    title: 'بازگشت به مبدا',
                    colClass: 'col-md-6 col-lg-3'
                  }
                },
                templateReadOnlyFields: {
                  waitMinute: {
                    title: 'توقف در محل',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  roundTrip: {
                    title: 'بازگشت به مبدا',
                    colClass: 'col-md-6 col-lg-3'
                  }
                },
                inputs: {
                  origin: {
                    name: {
                      title: 'تحویل دهنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lg-8 col-xl-4'
                    },
                    plate: {
                      title: 'پلاک',
                      validation: 'digit',
                      colClass: 'col-lg-2 col-xl-2'
                    },
                    unit: {
                      title: 'واحد',
                      validation: '',
                      colClass: 'col-lg-2 col-xl-2'
                    }
                  },
                  middlePoints: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه ',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lg-8 col-xl-4'
                    },
                    plate: {
                      title: 'پلاک ',
                      validation: 'digit',
                      colClass: 'col-lg-2 col-xl-2'
                    },
                    unit: {
                      title: 'واحد ',
                      validation: '',
                      colClass: 'col-lg-2 col-xl-2'
                    }
                  },
                  destination: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه ',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lg-8 col-xl-4'
                    },
                    plate: {
                      title: 'پلاک ',
                      validation: 'digit',
                      colClass: 'col-lg-2 col-xl-2'
                    },
                    unit: {
                      title: 'واحد ',
                      validation: '',
                      colClass: 'col-lg-2 col-xl-2'
                    }
                  },
                  description: {
                    title: 'توضیحات',
                    validation: ''
                  },
                  attachments: {
                    title: 'پیوست ها',
                    validation: ''
                  },
                  image: {
                    title: 'تصویر',
                    validation: ''
                  }
                },
                templateInputs: {
                  templateTitle: {
                    title: 'عنوان الگو',
                    validation: 'required'
                  },
                  origin: {
                    name: {
                      title: 'تحویل دهنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-lg-4 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-lg-4 col-xl-2'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lg-8 col-xl-6'
                    },
                    plate: {
                      title: 'پلاک',
                      validation: 'digit',
                      colClass: 'col-lg-2 col-xl-1'
                    },
                    unit: {
                      title: 'واحد',
                      validation: '',
                      colClass: 'col-lg-2 col-xl-1'
                    }
                  },
                  middlePoints: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه ',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    address: {
                      title: 'آدرس مقصد',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lg-8 col-xl-6'
                    },
                    plate: {
                      title: 'پلاک ',
                      validation: 'digit',
                      colClass: 'col-lg-2 col-xl-1'
                    },
                    unit: {
                      title: 'واحد ',
                      validation: '',
                      colClass: 'col-lg-2 col-xl-1'
                    }
                  },
                  destination: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-lg-4 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه ',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-lg-4 col-xl-2'
                    },
                    address: {
                      title: 'آدرس مقصد',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lg-8 col-xl-6'
                    },
                    plate: {
                      title: 'پلاک ',
                      validation: 'digit',
                      colClass: 'col-lg-2 col-xl-1'
                    },
                    unit: {
                      title: 'واحد ',
                      validation: '',
                      colClass: 'col-lg-2 col-xl-1'
                    }
                  },
                  description: {
                    title: 'توضیحات',
                    validation: ''
                  }
                },
                showPointDivider: true,
                originColClass: '',
                middlePointsColClass: '',
                destinationColClass: '',
                validation: [
                  {
                    point: 'origin',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  'selectedCourierIds'
                ],
                templateValidation: [
                  {
                    point: 'origin',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId', 'latitude', 'longitude']
                  }
                ],
                stepToGoOnValidationFailed: 'company-selection',
                templateStepToGoOnValidationFailed: 'property-selection',
                templateSectionToGoOnValidationFailed: 'specification'
              }
            },
            {
              progress: 100,
              templateProgress: 100,
              hideInTemplate: true,
              slug: 'pay',
              title: 'پرداخت',
              defaultIcon: 'step-4-deactive-dark.svg',
              activeIcon: 'deactiveCompleteAddress.svg',
              config: {
                validation: [
                  {
                    point: 'origin',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  'selectedCourierIds'
                ],
                templateValidation: [],
                stepToGoOnValidationFailed: 'overview',
                templateStepToGoOnValidationFailed: null,
                templateSectionToGoOnValidationFailed: null
              }
            },
            {
              progress: null,
              templateProgress: null,
              hideInTemplate: true,
              slug: 'success',
              title: 'ثبت موفقیت آمیز',
              config: {
                validation: ['acceptedOffers'],
                stepToGoOnValidationFailed: 'overview'
              }
            }
          ],
          enabled: true
        },
        {
          id: 50,
          title: loadTypes.pickup.title,
          slug: 'pickup',
          image: 'postage.svg',
          themeColor: 'pickup',
          steps: [
            {
              progress: 0,
              templateProgress: 0,
              hideInTemplate: false,
              slug: 'location-selection',
              title: 'انتخاب محل',
              defaultIcon: 'activeSelectLocation.svg',
              activeIcon: 'activeSelectLocation.svg',
              config: {
                type: 'map-based',
                showPlans: true,
                includeOrigin: true,
                originCoordinatesRequired: true,
                includeDestination: true,
                destinationCoordinatesRequired: true,
                showRangeMessage: true,
                rangeMessage: {
                  100: '', // this cause no message shown in urban range
                  200: 'این درخواست با قوانین و تعرفه <b>بین شهری</b> محاسبه خواهد شد'
                },
                origin: {
                  title: 'مبدا',
                  markerIcon: 'originMap.png',
                  fields: {
                    address: 'min:10'
                  }
                },
                destination: {
                  title: 'مقصد',
                  markerIcon: 'destination.svg',
                  fields: {
                    address: 'min:10'
                  }
                },
                multiDestination: true,
                showCompleteAddressButton: false,
                showCompleteAddressModalBeforeNext: false,
                manualCitySelectionOnException: false,
                validation: [],
                stepToGoOnValidationFailed: 'location-selection'
              }
            },
            {
              progress: 25,
              templateProgress: 50,
              hideInTemplate: false,
              slug: 'property-selection',
              title: 'تعیین ویژگی ها',
              defaultIcon: 'deactiveSelectShipment.svg',
              activeIcon: 'activeSelectShipment.svg',
              config: {
                indexSection: 'specification',
                templateIndexSection: 'specification',
                withSectionHeader: false,
                sections: [
                  {
                    title: 'مشخصات',
                    slug: 'specification',
                    showBrief: true,
                    hideInTemplate: false,
                    fields: {
                      waitMinute: {
                        title: 'توقف در محل',
                        validation: ''
                      },
                      roundTrip: {
                        title: 'بازگشت به مبدأ',
                        validation: ''
                      },
                      reserveOrder: {
                        title: 'رزرو درخواست برای آینده',
                        validation: ''
                      },
                      orderDate: {
                        title: 'تاریخ رزرو',
                        validation: 'required'
                      },
                      orderTime: {
                        title: 'ساعت رزرو',
                        validation: 'required'
                      }
                    }
                  }
                ],
                validation: [
                  {
                    point: 'origin',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId', 'latitude', 'longitude']
                  }
                ],
                templateValidation: [
                  {
                    point: 'origin',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId', 'latitude', 'longitude']
                  }
                ],
                stepToGoOnValidationFailed: 'location-selection',
                templateStepToGoOnValidationFailed: 'location-selection'
              }
            },
            {
              progress: 50,
              hideInTemplate: true,
              slug: 'company-selection',
              title: 'انتخاب شرکت (باربری)',
              defaultIcon: 'deactiveSelectShipment.svg',
              activeIcon: 'activeSelectShipment.svg',
              config: {
                initialSelectsCount: 0,
                multiSelect: false,
                noResultMessage:
                  'متاسفانه هیچ باربری ای مطابق با درخواست شما پیدا نشد!',
                sortsByRange: {
                  100: ['popular', 'name', 'cheapest', 'most-expensive'],
                  200: ['popular', 'name', 'cheapest', 'most-expensive']
                },
                defaultSort: {
                  100: 'cheapest',
                  200: 'popular'
                },
                description:
                  'لطفا باربری مورد نظر خود را برای ثبت نهایی انتخاب نمایید',
                validation: [
                  {
                    point: 'origin',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId', 'latitude', 'longitude']
                  }
                ],
                templateValidation: [
                  {
                    point: 'origin',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId', 'latitude', 'longitude']
                  }
                ],
                stepToGoOnValidationFailed: 'property-selection',
                sectionToGoOnValidationFailed: 'specification'
              }
            },
            {
              progress: 75,
              templateProgress: 100,
              hideInTemplate: false,
              slug: 'overview',
              title: 'بررسی اجمالی',
              defaultIcon: 'deactiveCompleteAddress.svg',
              activeIcon: 'activeCompleteAddress.svg',
              config: {
                readOnlyFields: {
                  waitMinute: {
                    title: 'توقف در محل',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  roundTrip: {
                    title: 'بازگشت به مبدا',
                    colClass: 'col-md-6 col-lg-3'
                  }
                },
                templateReadOnlyFields: {
                  waitMinute: {
                    title: 'توقف در محل',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  roundTrip: {
                    title: 'بازگشت به مبدا',
                    colClass: 'col-md-6 col-lg-3'
                  }
                },
                inputs: {
                  origin: {
                    name: {
                      title: 'تحویل دهنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lg-8 col-xl-4'
                    },
                    plate: {
                      title: 'پلاک',
                      validation: 'digit',
                      colClass: 'col-lg-2 col-xl-2'
                    },
                    unit: {
                      title: 'واحد',
                      validation: '',
                      colClass: 'col-lg-2 col-xl-2'
                    }
                  },
                  middlePoints: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه ',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    address: {
                      title: 'آدرس مقصد',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lg-8 col-xl-4'
                    },
                    plate: {
                      title: 'پلاک ',
                      validation: 'digit',
                      colClass: 'col-lg-2 col-xl-2'
                    },
                    unit: {
                      title: 'واحد ',
                      validation: '',
                      colClass: 'col-lg-2 col-xl-2'
                    }
                  },
                  destination: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه ',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    address: {
                      title: 'آدرس مقصد',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lg-8 col-xl-4'
                    },
                    plate: {
                      title: 'پلاک ',
                      validation: 'digit',
                      colClass: 'col-lg-2 col-xl-2'
                    },
                    unit: {
                      title: 'واحد ',
                      validation: '',
                      colClass: 'col-lg-2 col-xl-2'
                    }
                  },
                  description: {
                    title: 'توضیحات',
                    validation: ''
                  },
                  attachments: {
                    title: 'پیوست ها',
                    validation: ''
                  },
                  image: {
                    title: 'تصویر',
                    validation: ''
                  }
                },
                templateInputs: {
                  templateTitle: {
                    title: 'عنوان الگو',
                    validation: 'required'
                  },
                  origin: {
                    name: {
                      title: 'تحویل دهنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-lg-4 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-lg-4 col-xl-2'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lg-8 col-xl-6'
                    },
                    plate: {
                      title: 'پلاک',
                      validation: 'digit',
                      colClass: 'col-lg-2 col-xl-1'
                    },
                    unit: {
                      title: 'واحد',
                      validation: '',
                      colClass: 'col-lg-2 col-xl-1'
                    }
                  },
                  middlePoints: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه ',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lg-8 col-xl-6'
                    },
                    plate: {
                      title: 'پلاک ',
                      validation: 'digit',
                      colClass: 'col-lg-2 col-xl-1'
                    },
                    unit: {
                      title: 'واحد ',
                      validation: '',
                      colClass: 'col-lg-2 col-xl-1'
                    }
                  },
                  destination: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-lg-4 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه ',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-lg-4 col-xl-2'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lg-8 col-xl-6'
                    },
                    plate: {
                      title: 'پلاک ',
                      validation: 'digit',
                      colClass: 'col-lg-2 col-xl-1'
                    },
                    unit: {
                      title: 'واحد ',
                      validation: '',
                      colClass: 'col-lg-2 col-xl-1'
                    }
                  },
                  description: {
                    title: 'توضیحات',
                    validation: ''
                  }
                },
                showPointDivider: true,
                originColClass: '',
                middlePointsColClass: '',
                destinationColClass: '',
                validation: [
                  {
                    point: 'origin',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  'selectedCourierIds'
                ],
                templateValidation: [
                  {
                    point: 'origin',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId', 'latitude', 'longitude']
                  }
                ],
                stepToGoOnValidationFailed: 'company-selection',
                templateStepToGoOnValidationFailed: 'property-selection',
                templateSectionToGoOnValidationFailed: 'specification'
              }
            },
            {
              progress: 100,
              templateProgress: 100,
              hideInTemplate: true,
              slug: 'pay',
              title: 'پرداخت',
              defaultIcon: 'step-4-deactive-dark.svg',
              activeIcon: 'deactiveCompleteAddress.svg',
              config: {
                validation: [
                  {
                    point: 'origin',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId', 'latitude', 'longitude']
                  },
                  'selectedCourierIds'
                ],
                templateValidation: [],
                stepToGoOnValidationFailed: 'overview',
                templateStepToGoOnValidationFailed: null,
                templateSectionToGoOnValidationFailed: null
              }
            },
            {
              progress: null,
              templateProgress: null,
              hideInTemplate: true,
              slug: 'success',
              title: 'ثبت موفقیت آمیز',
              config: {
                validation: ['acceptedOffers'],
                stepToGoOnValidationFailed: 'overview'
              }
            }
          ],
          enabled: true
        },
        // material
        {
          id: 60,
          title: loadTypes.material.title,
          slug: 'material',
          image: 'furniture.svg',
          themeColor: 'material',
          steps: [
            {
              progress: 0,
              templateProgress: 0,
              hideInTemplate: false,
              slug: 'location-selection',
              title: 'انتخاب محل',
              defaultIcon: 'opened-box-dark.svg',
              activeIcon: 'opened-box-dark.svg',
              config: {
                type: 'map-based',
                showPlans: true,
                includeOrigin: true,
                originCoordinatesRequired: true,
                includeDestination: true,
                destinationCoordinatesRequired: true,
                showRangeMessage: true,
                rangeMessage: {
                  100: 'این درخواست با تعرفه ی <b>شهری</b> محاسبه خواهد شد',
                  200: 'این درخواست با تعرفه ی <b>بین شهری</b> محاسبه خواهد شد'
                },
                origin: {
                  title: 'مبدا',
                  markerIcon: 'originMap.png',
                  fields: []
                },
                destination: {
                  title: 'مقصد',
                  markerIcon: 'destination.svg',
                  fields: []
                },
                multiDestination: true,
                showCompleteAddressButton: false,
                showCompleteAddressModalBeforeNext: false,
                manualCitySelectionOnException: true,
                validation: [],
                stepToGoOnValidationFailed: 'location-selection'
              }
            },
            {
              progress: 50,
              templateProgress: 50,
              hideInTemplate: false,
              slug: 'property-selection',
              title: 'تعیین ویژگی ها',
              defaultIcon: 'deactiveSelectItem.svg',
              activeIcon: 'activeSelectItem.svg',
              config: {
                indexSection: 'material-specification',
                templateIndexSection: 'material-specification',
                withSectionHeader: false,
                sections: [
                  {
                    title: 'مشخصات بار',
                    slug: 'material-specification',
                    showBrief: true,
                    hideInTemplate: false,
                    fields: {
                      loadType: {
                        title: 'نوع بار',
                        validation: 'required'
                      },
                      packageType: {
                        title: 'نوع بسته‌بندی',
                        validation: 'required'
                      },
                      monetaryValue: {
                        title: 'ارزش بار',
                        validation:
                          'required|decimal|min_value: 1|validCurrency'
                      },
                      weightKilogram: {
                        title: 'وزن بار',
                        validation: 'required|min_value:0.1|max_value:100'
                      },
                      reserveOrder: {
                        title: 'رزرو درخواست برای آینده',
                        validation: ''
                      },
                      orderDate: {
                        title: 'تاریخ بارگیری',
                        validation: 'required'
                      },
                      orderTime: {
                        title: 'ساعت بارگیری',
                        validation: 'required'
                      }
                    }
                  },
                  {
                    title: 'خودرو',
                    slug: 'car',
                    type: 'load-based',
                    showBrief: true,
                    hideInTemplate: false,
                    fields: {}
                  }
                ],
                validation: [
                  {
                    point: 'origin',
                    fields: ['latitude', 'longitude']
                  },
                  {
                    point: 'destination',
                    fields: ['latitude', 'longitude']
                  }
                ],
                templateValidation: [
                  {
                    point: 'origin',
                    fields: ['latitude', 'longitude']
                  },
                  {
                    point: 'destination',
                    fields: ['latitude', 'longitude']
                  }
                ],
                stepToGoOnValidationFailed: 'location-selection',
                templateStepToGoOnValidationFailed: 'location-selection'
              }
            },
            {
              progress: 100,
              templateProgress: 100,
              hideInTemplate: false,
              slug: 'overview',
              title: 'بررسی اجمالی',
              defaultIcon: 'deactiveCompleteAddress.svg',
              activeIcon: 'activeCompleteAddress.svg',
              config: {
                readOnlyFields: {
                  orderDate: 'تاریخ بارگیری',
                  orderTime: 'ساعت بارگیری',
                  car: {
                    title: 'خودرو',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  carLoad: {
                    title: 'نوع بارگیر',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  load: {
                    title: 'نوع بار',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  packageType: {
                    title: 'بسته‌بندی',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  weight: {
                    title: 'وزن بار',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  monetaryValue: {
                    title: 'ارزش بار',
                    colClass: 'col-md-6 col-lg-3'
                  }
                },
                templateReadOnlyFields: {
                  origin: {
                    address: 'محل بارگیری'
                  },
                  destination: {
                    address: 'محل تخلیه'
                  },
                  car: {
                    title: 'خودرو',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  carLoad: {
                    title: 'نوع بارگیر',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  load: {
                    title: 'نوع بار',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  packageType: {
                    title: 'بسته‌بندی',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  weight: {
                    title: 'وزن بار',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  monetaryValue: {
                    title: 'ارزش بار',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  document: {
                    title: 'نوع سند حمل',
                    colClass: 'col-md-6 col-lg-3'
                  }
                },
                inputs: {
                  origin: {
                    name: {
                      title: 'تحویل دهنده',
                      validation: 'required',
                      colClass: 'col-md-12 col-lg-12 col-xl-3'
                    },
                    mobile: {
                      title: 'تلفن همراه',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-lg-6 col-xl-3'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lg-8 col-xl-4'
                    },
                    plate: {
                      title: 'پلاک',
                      validation: 'digit',
                      colClass: 'col-lg-2 col-xl-2'
                    }
                  },
                  middlePoints: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-lg-6 col-xl-3'
                    },
                    mobile: {
                      title: 'تلفن همراه ',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-lg-6 col-xl-3'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lg-9 col-xl-4'
                    },
                    plate: {
                      title: 'پلاک ',
                      validation: 'digit',
                      colClass: 'col-lg-2 col-xl-2'
                    }
                  },
                  destination: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-lg-6 col-xl-3'
                    },
                    mobile: {
                      title: 'تلفن همراه ',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-lg-6 col-xl-3'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lg-8 col-xl-4'
                    },
                    plate: {
                      title: 'پلاک ',
                      validation: 'digit',
                      colClass: 'col-lg-2 col-xl-2'
                    }
                  },
                  description: {
                    title: 'توضیحات',
                    validation: '',
                    colClass: ''
                  },
                  attachments: {
                    title: 'پیوست ها',
                    validation: ''
                  },
                  image: {
                    title: 'تصویر',
                    validation: ''
                  }
                },
                templateInputs: {
                  templateTitle: {
                    title: 'عنوان الگو',
                    validation: 'required'
                  },
                  origin: {
                    name: {
                      title: 'تحویل دهنده',
                      validation: 'required',
                      colClass: 'col-md-6'
                    },
                    mobile: {
                      title: 'تلفن همراه',
                      validation: 'required|mobile',
                      colClass: 'col-md-6'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12'
                    }
                  },

                  middlePoints: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه ',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-lg-6 col-xl-2'
                    },
                    address: {
                      title: 'آدرس مقصد',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lg-8 col-xl-6'
                    },
                    plate: {
                      title: 'پلاک ',
                      validation: 'digit',
                      colClass: 'col-lg-2 col-xl-1'
                    }
                  },
                  destination: {
                    name: {
                      title: 'تحویل گیرنده',
                      validation: 'required',
                      colClass: 'col-md-6'
                    },
                    mobile: {
                      title: 'تلفن همراه ',
                      validation: 'required|mobile',
                      colClass: 'col-md-6'
                    },
                    address: {
                      title: 'آدرس مقصد',
                      validation: 'required|min:10',
                      colClass: 'col-md-12'
                    }
                  },
                  description: {
                    title: 'توضیحات',
                    validation: '',
                    colClass: ''
                  }
                },
                showPointDivider: true,
                originColClass: 'col-lg-12',
                destinationColClass: 'col-lg-12',
                validation: [
                  {
                    point: 'origin',
                    fields: ['cityId']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId']
                  },
                  'orderTime',
                  'loadId',
                  'packageId',
                  'monetaryValue',
                  'weightKilogram',
                  'car'
                ],
                templateValidation: [
                  {
                    point: 'origin',
                    fields: ['cityId']
                  },
                  {
                    point: 'destination',
                    fields: ['cityId']
                  },
                  'loadId',
                  'packageId',
                  'monetaryValue',
                  'weightKilogram',
                  'car'
                ],
                stepToGoOnValidationFailed: 'overview',
                templateStepToGoOnValidationFailed: 'property-selection',
                templateSectionToGoOnValidationFailed: 'specification'
              }
            },
            {
              progress: null,
              templateProgress: null,
              hideInTemplate: true,
              slug: 'success',
              title: 'ثبت موفقیت آمیز',
              config: {
                validation: ['acceptedOffers'],
                stepToGoOnValidationFailed: 'overview'
              }
            }
          ],
          enabled: true
        },
        // trash
        {
          id: 80,
          title: loadTypes.trash.title,
          slug: 'trash',
          image: 'furniture.svg',
          themeColor: '#795548',
          steps: [
            {
              progress: 0,
              templateProgress: 0,
              hideInTemplate: false,
              slug: 'location-selection',
              title: 'انتخاب محل',
              defaultIcon: 'opened-box-dark.svg',
              activeIcon: 'opened-box-dark.svg',
              config: {
                type: 'map-based',
                showPlans: true,
                includeOrigin: true,
                originCoordinatesRequired: true,
                includeDestination: false,
                destinationCoordinatesRequired: false,
                showRangeMessage: false,
                origin: {
                  title: 'محل جمع آوری',
                  markerIcon: 'originMap.png',
                  fields: {
                    address: 'min:10'
                  }
                },
                destination: null,
                multiDestination: false,
                showCompleteAddressButton: true,
                showCompleteAddressModalBeforeNext: false,
                manualCitySelectionOnException: true,
                validation: [],
                stepToGoOnValidationFailed: 'location-selection'
              }
            },
            {
              progress: 50,
              templateProgress: 50,
              hideInTemplate: false,
              slug: 'property-selection',
              title: 'تعیین ویژگی ها',
              defaultIcon: 'deactiveSelectItem.svg',
              activeIcon: 'activeSelectItem.svg',
              config: {
                indexSection: 'timing',
                templateIndexSection: 'timing',
                withSectionHeader: false,
                sections: [
                  {
                    title: 'تاریخ و زمان',
                    slug: 'timing',
                    showBrief: true,
                    hideInTemplate: false,
                    fields: {
                      orderDate: {
                        title: 'تاریخ جمع آوری',
                        validation: 'required'
                      },
                      orderTime: {
                        title: 'ساعت جمع آوری',
                        validation: 'required'
                      }
                    }
                  },
                  {
                    title: 'مشخصات بار',
                    slug: 'trash-specification',
                    showBrief: true,
                    hideInTemplate: false,
                    fields: {}
                  }
                ],
                validation: [
                  {
                    point: 'origin',
                    fields: ['latitude', 'longitude']
                  }
                ],
                templateValidation: [
                  {
                    point: 'origin',
                    fields: ['latitude', 'longitude']
                  }
                ],
                stepToGoOnValidationFailed: 'location-selection',
                templateStepToGoOnValidationFailed: 'location-selection'
              }
            },
            {
              progress: 100,
              templateProgress: 100,
              hideInTemplate: false,
              slug: 'overview',
              title: 'بررسی اجمالی',
              defaultIcon: 'deactiveCompleteAddress.svg',
              activeIcon: 'activeCompleteAddress.svg',
              config: {
                readOnlyFields: {
                  orderDate: 'تاریخ جمع آوری',
                  orderTime: 'ساعت جمع آوری',
                  car: {
                    title: 'خودرو',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  packageTypes: {
                    title: 'بسته بندی ها',
                    colClass: 'col-md-6 col-lg-3'
                  }
                },
                templateReadOnlyFields: {
                  car: {
                    title: 'خودرو',
                    colClass: 'col-md-6 col-lg-3'
                  },
                  packageTypes: {
                    title: 'بسته‌بندی ها',
                    colClass: 'col-md-6 col-lg-3'
                  }
                },
                inputs: {
                  origin: {
                    name: {
                      title: 'تحویل دهنده',
                      validation: 'required',
                      colClass: 'col-md-12 col-lg-12 col-xl-3'
                    },
                    mobile: {
                      title: 'تلفن همراه',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-lg-6 col-xl-3'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-lg-8 col-xl-4'
                    }
                  },
                  description: {
                    title: 'توضیحات',
                    validation: '',
                    colClass: ''
                  },
                  attachments: {
                    title: 'پیوست ها',
                    validation: ''
                  },
                  image: {
                    title: 'تصویر',
                    validation: ''
                  }
                },
                templateInputs: {
                  templateTitle: {
                    title: 'عنوان الگو',
                    validation: 'required'
                  },
                  origin: {
                    name: {
                      title: 'تحویل دهنده',
                      validation: 'required',
                      colClass: 'col-md-6 col-xl-2'
                    },
                    mobile: {
                      title: 'تلفن همراه',
                      validation: 'required|mobile',
                      colClass: 'col-md-6 col-xl-2'
                    },
                    address: {
                      title: 'آدرس',
                      validation: 'required|min:10',
                      colClass: 'col-md-12 col-xl-6'
                    }
                  },
                  description: {
                    title: 'توضیحات',
                    validation: ''
                  },
                  attachments: {
                    title: 'پیوست ها',
                    validation: ''
                  },
                  image: {
                    title: 'تصویر',
                    validation: ''
                  }
                },
                showPointDivider: true,
                originColClass: 'col-lg-12',
                destinationColClass: 'col-lg-12',
                validation: [
                  {
                    point: 'origin',
                    fields: ['cityId']
                  },
                  'orderTime',
                  {
                    or: ['car', 'packageType']
                  }
                ],
                templateValidation: [
                  {
                    point: 'origin',
                    fields: ['cityId']
                  },
                  'orderTime',
                  {
                    or: ['car', 'packageType']
                  }
                ],
                stepToGoOnValidationFailed: 'property-selection',
                templateStepToGoOnValidationFailed: 'property-selection'
              }
            }
          ],
          enabled: true
        }
      ],
      properties: {
        orderTypeId: null,
        orderRangeId: null,
        selectedCourier: null,
        withCar: true
      },
      staticLists: {
        dayPart: [
          {
            name: 'کل روز',
            value: '1',
            minHour: '00:00:00',
            maxHour: '23:59:59'
          },
          {
            name: 'بامداد',
            value: '10',
            minHour: '00:00:00',
            maxHour: '05:59:59'
          },
          {
            name: 'صبح',
            value: '20',
            minHour: '06:00:00',
            maxHour: '09:59:59'
          },
          {
            name: 'ظهر',
            value: '30',
            minHour: '10:00:00',
            maxHour: '13:59:59'
          },
          {
            name: 'بعدازظهر',
            value: '40',
            minHour: '14:00:00',
            maxHour: '16:59:59'
          },
          {
            name: 'عصر',
            value: '50',
            minHour: '17:00:00',
            maxHour: '19:59:59'
          },
          { name: 'شب', value: '60', minHour: '20:00:00', maxHour: '23:59:59' }
        ],
        document: [{ name: 'بارنامه', value: 1 }],
        postagePackageType: [
          {
            name: 'کارتن',
            value: 1,
            enum: 'PACK',
            icon: '',
            weightDetermination: 1000,
            weightLabel: 'کیلوگرم',
            weightMin: 0.5,
            weightMax: 25,
            isKiloGrams: true
          },
          {
            name: 'پاکت',
            value: 2,
            enum: 'ENVELOPE',
            icon: '',
            weightDetermination: 0,
            weightLabel: 'وزن (گرم)',
            weightMin: 1,
            weightMax: 5000,
            isKiloGrams: false
          }
        ],
        waitMinute: [
          { id: null, title: 'بدون توقف', name: 'بدون توقف', value: null },
          { id: 5, title: '5 دقیقه', name: '5 دقیقه', value: 5 },
          { id: 10, title: '10 دقیقه', name: '10 دقیقه', value: 10 },
          { id: 15, title: '15 دقیقه', name: '15 دقیقه', value: 15 },
          { id: 20, title: '20 دقیقه', name: '20 دقیقه', value: 20 },
          { id: 30, title: '30 دقیقه', name: '30 دقیقه', value: 30 },
          { id: 60, title: '1 ساعت', name: '1 ساعت', value: 60 },
          { id: 120, title: '2 ساعت', name: '2 ساعت', value: 120 }
        ]
      },
      fetchedLists: {
        car: {
          filter: {
            typeId: null,
            rangeId: null
          },
          list: []
        },
        employee: {
          filter: {
            typeId: null,
            rangeId: null
          },
          list: []
        },
        item: {
          filter: {
            typeId: null,
            rangeId: null
          },
          list: []
        },
        load: {
          filter: {
            typeId: null,
            rangeId: null
          },
          list: []
        },
        carLoad: {
          filter: {
            typeId: null,
            rangeId: null
          },
          list: []
        }
      },
      data: {
        origin: {
          cityId: null,
          latitude: null,
          longitude: null,
          address: null,
          addressId: null,
          name: null,
          mobile: null,
          phone: null,
          zipCode: null,
          plate: null,
          floor: null,
          unit: null,
          elevator: false,
          carDistanceMeter: 10,
          postalCode: null,
          originWeightOfWeighbridge: null,
          originWeighbridgeReceipt: null
        },
        destination: {
          cityId: null,
          latitude: null,
          longitude: null,
          address: null,
          addressId: null,
          name: null,
          mobile: null,
          phone: null,
          zipCode: null,
          plate: null,
          floor: null,
          unit: null,
          elevator: false,
          carDistanceMeter: 10,
          postalCode: null,
          destinationWeightOfWeighbridge: null,
          destinationWeighbridgeReceipt: null
        },
        attachments: null,
        image: null,
        middlePoints: [],
        typeId: null,
        rangeId: null,
        orderTime: null,
        dischargeTime: null,
        dischargeDayPartId: null,
        car: [],
        packageType: [],
        employee: [],
        item: [],
        packagge: [
          {
            typeId: {
              name: 'کارتن',
              value: 1,
              enum: 'PACK',
              icon: '',
              weightDetermination: 1000,
              weightLabel: 'کیلوگرم',
              weightMin: 0.5,
              weightMax: 25,
              isKiloGrams: true
            },
            count: 1,
            weightGram: null,
            monetaryValue: null,
            content: null
          }
        ],
        loadId: null,
        packageId: null,
        monetaryValue: null,
        weightKilogram: null,
        heightMeter: null,
        lengthMeter: null,
        widthMeter: null,
        templateId: null,
        estimatedDistanceMeter: null,
        estimatedDurationSecond: null,
        estimatedPolyline: [],
        courierIds: null,
        initiatedCourierId: null,
        description: null,
        title: null,
        timeSheetTemplateId: null,
        timeSheetDate: null,
        sendMethodId: null,
        waitMinute: null,
        roundTrip: false,
        reserveOrder: false,
        // helper fields
        userId: null,
        selectedCourierIds: [],
        selectedTimeSheetTemplate: null,
        acceptedOffers: []
      }
    },
    userObject: null,
    orderObject: {},
    orderConfig: null,
    orderStep: null,
    orderStepSection: null,
    orderDataCache: {},
    mapOptions: null
  },
  actions,
  mutations,
  getters,
  strict: debug,
  plugins: debug ? [createLogger()] : []
})
